import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getApiBase, withBase } from '../config/api';
import '../styles/admin.css';
import fallbackImage from '../assets/fallback.jpg';

// --- Reusable Components (from ManageProducts) ---
const Message = ({ text, type }) => {
  if (!text) return null;
  return <div className={`alert alert-${type} mt-2`}>{text}</div>;
};

const ImageUploadForm = ({ categoryId, onImageChange }) => {
  const [files, setFiles] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    setFiles(e.target.files);
    setError('');
    setSuccess('');
  };

  const handleUpload = async () => {
    if (!files || files.length === 0) {
      setError('Please select one or more images to upload.');
      return;
    }
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('images', files[i]);
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post(`${getApiBase()}/api/gallery-categories/${categoryId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(res.data.message || `${files.length} images uploaded.`);
      onImageChange();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload images.');
    } finally {
      setLoading(false);
      setFiles(null);
      if (fileInputRef.current) fileInputRef.current.value = null;
    }
  };

  return (
    <div className="mb-3">
      <label className="form-label fw-bold">Add Images to this Category</label>
      <div className="input-group">
        <input
          type="file"
          className="form-control"
          id={`file-input-${categoryId}`}
          ref={fileInputRef}
          accept="image/*"
          multiple
          onChange={handleFileChange}
        />
        <button
          className="btn btn-admin-primary"
          type="button"
          onClick={handleUpload}
          disabled={loading}
        >
          {loading ? 'Uploading...' : 'Upload'}
        </button>
      </div>
      <small className="text-muted">You can select multiple images at once. Max 5MB each.</small>
      {error && <Message text={error} type="danger" />}
      {success && <Message text={success} type="success" />}
    </div>
  );
};

const ImagesTable = ({ images, onImageChange }) => {
  const handleDeleteImage = async (imageId) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await axios.delete(`${getApiBase()}/api/gallery-images/${imageId}`);
        onImageChange();
      } catch (err) {
        alert('Failed to delete image.');
      }
    }
  };

  if (images.length === 0) {
    return <p className="text-muted small">No images in this category yet.</p>;
  }

  return (
    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
      <table className="table table-sm table-striped align-middle">
        <thead>
          <tr>
            <th style={{width: '120px'}}>Image</th>
            <th>Label (Filename)</th>
            <th style={{width: '120px'}}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {images.map((img) => (
            <tr key={img.id}>
              <td>
                <img
                  src={withBase(img.src)}
                  className="img-fluid rounded"
                  alt={img.label}
                  style={{ height: '60px', width: '100px', objectFit: 'cover' }}
                />
              </td>
              <td className="text-muted small" style={{wordBreak: 'break-all'}}>{img.label}</td>
              <td>
                {/* Replace button is not built for this component, but can be added later */}
                <button
                  className="btn btn-sm btn-outline-danger ms-2"
                  onClick={() => handleDeleteImage(img.id)}
                  aria-label="Delete image"
                  title="Delete Image"
                >
                  🗑️
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// --- Modal for Managing a Category ---
const ManageCategoryModal = ({ category, onClose, onCategoryDeleted, onImageChange }) => {
  if (!category) return null;

  const handleDeleteCategory = async () => {
    if (window.confirm(`Are you sure you want to delete "${category.name}"? This will delete all its images.`)) {
      try {
        await axios.delete(`${getApiBase()}/api/gallery-categories/${category.id}`);
        onCategoryDeleted();
        onClose();
      } catch (err) {
        alert('Failed to delete category.');
      }
    }
  };

  return (
    <div className="prod-admin-modal-overlay" onClick={onClose}>
      <div className="prod-admin-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="prod-admin-modal-header">
          <h5 className="prod-admin-modal-title">Manage: {category.name}</h5>
          <button type="button" className="prod-admin-btn-close" onClick={onClose}></button>
        </div>
        <div className="prod-admin-modal-body">
          <ImageUploadForm categoryId={category.id} onImageChange={onImageChange} />
          <hr />
          <h6 className="mb-3">Existing Images ({category.images.length})</h6>
          <ImagesTable images={category.images} onImageChange={onImageChange} />
        </div>
        <div className="prod-admin-modal-footer justify-content-between">
          <button className="btn btn-outline-danger" onClick={handleDeleteCategory}>
            Delete Entire Category
          </button>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Form to Create a New Category ---
const CreateCategoryForm = ({ onCategoryCreated }) => {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name) {
      setError('Category name is required.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await axios.post(`${getApiBase()}/api/gallery-categories`, { name });
      setName('');
      setLoading(false);
      setSuccess(`Category "${name}" created successfully!`);
      onCategoryCreated();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create category.');
      setLoading(false);
    }
  };

  return (
    <div className="admin-card mb-4">
      <div className="card-body">
        <h5 className="mb-3">Create New Gallery Category</h5>
        <form onSubmit={handleSubmit} className="row g-3">
          <div className="col-md-9">
            <label htmlFor="categoryName" className="form-label">Category Name</label>
            <input
              type="text"
              className="form-control"
              id="categoryName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Surya Sarees"
            />
          </div>
          <div className="col-md-3 d-flex align-items-end">
            <button type="submit" className="btn btn-admin-primary w-100" disabled={loading}>
              {loading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
        {error && <Message text={error} type="danger" />}
        {success && <Message text={success} type="success" />}
      </div>
    </div>
  );
};

// --- Main Page Component ---
const ManageGallery = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${getApiBase()}/api/gallery-all`);
      setCategories(res.data.categories || []);
      setError('');
    } catch (err) {
      setError('Failed to load gallery data.');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshData = async () => {
    try {
      const res = await axios.get(`${getApiBase()}/api/gallery-all`);
      setCategories(res.data.categories || []);
      
      if (selectedCategory) {
        const updatedCategory = (res.data.categories || []).find(cat => cat.id === selectedCategory.id);
        if (updatedCategory) {
          setSelectedCategory(updatedCategory);
        } else {
          setSelectedCategory(null);
        }
      }
    } catch (err) {
      console.error("Failed to refresh data:", err);
    }
  };
  
  const handleFullRefresh = () => {
    setLoading(true);
    fetchData();
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading gallery...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container py-5">
        <h1 className="display-4 fw-bold mb-3 admin-dashboard-title">Manage Brand Gallery</h1>
        
        <CreateCategoryForm onCategoryCreated={handleFullRefresh} />
        
        {error && <Message text={error} type="danger" />}
        
        <h3 className="mb-3" style={{ borderBottom: '2px solid #eee', paddingBottom: '10px' }}>
          Manage Existing Categories
        </h3>
        {categories.length === 0 ? (
          <p className="text-muted">No gallery categories created yet.</p>
        ) : (
          <div className="row g-3">
            {categories.map((category) => {
              const coverImage = category.images.length > 0 
                ? withBase(category.images[0].src) 
                : fallbackImage;
              
              return (
                <div key={category.id} className="col-md-4 col-lg-3">
                  <div 
                    className="prod-admin-grid-card" 
                    onClick={() => setSelectedCategory(category)}
                  >
                    <img 
                      src={coverImage} 
                      alt={category.name} 
                      className="img-fluid rounded-top" 
                      style={{height: '200px', width: '100%', objectFit: 'cover'}}
                    />
                    <div className="p-3">
                      <h6 className="mb-1" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {category.name}
                      </h6>
                      <small className="text-muted">{category.images.length} images</small>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedCategory && (
        <ManageCategoryModal
          category={selectedCategory}
          onClose={() => setSelectedCategory(null)}
          onCategoryDeleted={handleFullRefresh}
          onImageChange={refreshData}
        />
      )}
    </>
  );
};

export default ManageGallery;